import { lineName } from './data/items';
import { fmt, formatClockTime, formatDate } from './format';
import { translate } from './i18n';
import type { AppSettings, Lang, Order } from './types';

export interface InvoiceContext {
  order: Order;
  settings: AppSettings;
  /** Already resolved for the active language by the caller. */
  tableLabel: string;
  lang: Lang;
}

/** Escapes text before it goes into the invoice HTML. */
function esc(value: string | number): string {
  return String(value).replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;'
      : c === '<' ? '&lt;'
      : c === '>' ? '&gt;'
      : c === '"' ? '&quot;'
      : '&#39;',
  );
}

/** "Order #14", or the local number while the order is still in the outbox. */
export function invoiceNumber(order: Order, lang: Lang): string {
  // 'order_no' is a template ("Order #{n}"), not a prefix.
  const n = order.orderNo ?? order.localNo;
  const base = translate(lang, 'order_no', { n });
  return order.orderNo === null ? `${base} (${translate(lang, 'not_synced')})` : base;
}

/**
 * The printable bill, as a standalone HTML document.
 *
 * Sized for an 80mm thermal roll — the common counter printer — and it still
 * prints sensibly on A4 because the body is centred with a max width rather
 * than a fixed page size. Everything is inline: a print window has no chance to
 * fetch a stylesheet before the dialog opens.
 *
 * The menu prices are GST-inclusive (it says so on the physical menu), so the
 * bill states that rather than adding tax on top. If that ever changes, the tax
 * lines belong here and in `cartTotals`, not in the template.
 */
export function invoiceHtml({ order, settings, tableLabel, lang }: InvoiceContext): string {
  const t = (k: Parameters<typeof translate>[1], v?: Record<string, string | number>) =>
    esc(translate(lang, k, v));

  const placed = new Date(order.placedAt);
  const customer = order.customer.name.trim();
  const phone = order.customer.phone.trim();

  const lines = order.items
    .map((l) => {
      const base = lineName(l, lang);
      const name = l.variant && l.variant !== 'Price' ? `${base} (${l.variant})` : base;
      const saved = l.basePrice > l.price;
      return `
        <tr>
          <td class="n">${esc(name)}${
            saved
              ? `<div class="off"><s>${esc(fmt(l.basePrice))}</s> · −${l.offerPct}%</div>`
              : ''
          }</td>
          <td class="q">${l.qty}</td>
          <td class="r">${esc(fmt(l.price))}</td>
          <td class="a">${esc(fmt(l.price * l.qty))}</td>
        </tr>`;
    })
    .join('');

  const totalRow = (label: string, value: string, cls = '') =>
    `<tr class="${cls}"><td colspan="3">${label}</td><td class="a">${value}</td></tr>`;

  return `<!doctype html>
<html lang="${esc(lang)}">
<head>
<meta charset="utf-8">
<title>${esc(settings.brandName)} — ${invoiceNumber(order, lang)}</title>
<style>
  @page { margin: 6mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0 auto; padding: 10px; max-width: 80mm;
    font: 12px/1.45 -apple-system, "Segoe UI", Roboto, "Noto Sans Gujarati",
          "Noto Sans Devanagari", sans-serif;
    color: #000; background: #fff;
  }
  h1 { font-size: 17px; margin: 0 0 2px; letter-spacing: .2px; }
  .meta { font-size: 11px; color: #333; }
  .center { text-align: center; }
  hr { border: 0; border-top: 1px dashed #999; margin: 9px 0; }
  table { width: 100%; border-collapse: collapse; }
  th { font-size: 10px; text-transform: uppercase; letter-spacing: .4px;
       text-align: left; padding-bottom: 4px; border-bottom: 1px solid #000; }
  td { padding: 4px 0; vertical-align: top; }
  .q { width: 34px; text-align: center; }
  .r { width: 62px; text-align: right; }
  .a { width: 68px; text-align: right; white-space: nowrap; }
  /* Only the old price is struck; the percentage has to stay readable. */
  .off { font-size: 10px; color: #555; }
  .totals td { padding: 2px 0; }
  .totals tr td:first-child { text-align: right; padding-right: 8px; }
  .grand td { font-size: 15px; font-weight: 700; padding-top: 7px;
              border-top: 1px solid #000; }
  .note { font-size: 10px; color: #444; margin-top: 9px; }
  /* A browser prints its own header and footer; the bill supplies its own. */
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="center">
    <h1>${esc(settings.brandName)}</h1>
    ${settings.address ? `<div class="meta">${esc(settings.address)}</div>` : ''}
    ${settings.phone ? `<div class="meta">${esc(settings.phone)}</div>` : ''}
    ${settings.gstin ? `<div class="meta">${t('gstin')}: ${esc(settings.gstin)}</div>` : ''}
  </div>

  <hr>

  <div class="meta">
    <strong>${invoiceNumber(order, lang)}</strong><br>
    ${esc(tableLabel)}${customer ? ` · ${esc(customer)}` : ''}${phone ? ` · ${esc(phone)}` : ''}<br>
    ${esc(formatDate(placed))} · ${esc(formatClockTime(placed))}
  </div>

  <hr>

  <table>
    <thead>
      <tr>
        <th>${t('items_label')}</th>
        <th class="q">${t('qty_short')}</th>
        <th class="r">${t('rate')}</th>
        <th class="a">${t('amount')}</th>
      </tr>
    </thead>
    <tbody>${lines}</tbody>
  </table>

  <hr>

  <table class="totals">
    ${totalRow(t('subtotal'), esc(fmt(order.subtotal)))}
    ${order.itemSavings > 0 ? totalRow(t('item_savings'), `−${esc(fmt(order.itemSavings))}`) : ''}
    ${
      order.discountAmount > 0
        ? totalRow(
            `${t('discount')} (${order.discountPct}%)`,
            `−${esc(fmt(order.discountAmount))}`,
          )
        : ''
    }
    ${totalRow(t('total'), esc(fmt(order.total)), 'grand')}
  </table>

  <div class="note center">${t('gst_note')}</div>
  <div class="note center">${t('invoice_thanks')}</div>
</body>
</html>`;
}
